import { useState, useEffect } from "react";

export type Payment = {
  id: string;
  tenant_id: string;
  client_id: string;
  contract_id: string;
  payment_date: string;
  amount: number;
  method: string;
  reference: string;
  notes: string;
  status: string;
  created_at: string;
  updated_at: string;
  client_name: string;
  contract_number: string;
};

interface Props {
  payment: Payment | null;
  isNew: boolean;
  clients: { id: string; full_name: string }[];
  contracts: { id: string; client_id: string; contract_number: string }[];
  onClose: () => void;
  onSave: (data: Payment) => void;
}

const makeBlank = (): Payment => ({
  id: "",
  tenant_id: "",
  client_id: "",
  contract_id: "",
  payment_date: new Date().toISOString().split("T")[0],
  amount: 0,
  method: "cash",
  reference: "",
  notes: "",
  status: "confirmed",
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
  client_name: "",
  contract_number: "",
});

const INPUT =
  "w-full bg-white border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] placeholder-slate-400 outline-none focus:border-[#C8A96E]/60 transition-colors";

const METHODS = [
  { value: "cash",     label: "Cash",          icon: "ri-money-dollar-circle-line" },
  { value: "card",     label: "Credit/Debit Card", icon: "ri-bank-card-line" },
  { value: "zelle",    label: "Zelle",          icon: "ri-exchange-dollar-line" },
  { value: "venmo",    label: "Venmo",          icon: "ri-smartphone-line" },
  { value: "transfer", label: "Bank Transfer",  icon: "ri-bank-line" },
  { value: "check",    label: "Check",          icon: "ri-file-list-3-line" },
  { value: "other",    label: "Other",          icon: "ri-more-2-line" },
];

const STATUSES = [
  { value: "confirmed",  label: "Confirmed",  color: "text-emerald-600 bg-emerald-50" },
  { value: "pending",    label: "Pending",    color: "text-amber-600 bg-amber-50" },
  { value: "paid",       label: "Paid",       color: "text-emerald-600 bg-emerald-50" },
  { value: "void",       label: "Void",       color: "text-slate-500 bg-slate-50" },
  { value: "cancelled",  label: "Cancelled",  color: "text-red-600 bg-red-50" },
  { value: "refunded",   label: "Refunded",   color: "text-violet-600 bg-violet-50" },
];

export default function PaymentModal({ payment, isNew, clients, contracts, onClose, onSave }: Props) {
  const [form, setForm] = useState<Payment>(makeBlank());

  useEffect(() => {
    setForm(payment ? { ...makeBlank(), ...payment } : makeBlank());
  }, [payment]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => {
      const updated = { ...prev, [name]: name === "amount" ? Number(value) : value };
      if (name === "client_id") {
        const client = clients.find((c) => c.id === value);
        updated.client_name = client?.full_name ?? "";
        updated.contract_id = "";
        updated.contract_number = "";
      }
      if (name === "contract_id") {
        const contract = contracts.find((c) => c.id === value);
        updated.contract_number = contract?.contract_number ?? "";
      }
      return updated;
    });
  };

  const clientContracts = contracts.filter((c) => c.client_id === form.client_id);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave({ ...form, updated_at: new Date().toISOString() });
  };

  const isVoided = form.status === "void" || form.status === "cancelled";

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-lg overflow-hidden max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C8A96E]/10">
              <i className="ri-money-dollar-circle-line text-[#C8A96E] text-sm"></i>
            </div>
            <h2 className="text-[#0f172a] font-semibold text-base">
              {isNew ? "New Payment" : "Edit Payment"}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto flex-1 space-y-4">
          {/* Void warning */}
          {isVoided && (
            <div className="flex items-center gap-2.5 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
              <i className="ri-error-warning-line text-red-500 text-sm"></i>
              <p className="text-red-700 text-xs font-medium">
                This payment is {form.status} — it will not count toward the contract balance.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            {/* Client */}
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">
                Client *
              </label>
              <select
                name="client_id"
                value={form.client_id}
                onChange={handleChange}
                required
                className={INPUT + " cursor-pointer"}
              >
                <option value="">Select client…</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.full_name}</option>
                ))}
              </select>
            </div>

            {/* Contract */}
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">
                Contract
              </label>
              <select
                name="contract_id"
                value={form.contract_id}
                onChange={handleChange}
                className={INPUT + " cursor-pointer"}
                disabled={!form.client_id}
              >
                <option value="">No contract</option>
                {clientContracts.map((c) => (
                  <option key={c.id} value={c.id}>{c.contract_number}</option>
                ))}
              </select>
            </div>

            {/* Date */}
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">
                Payment Date *
              </label>
              <input
                name="payment_date"
                type="date"
                value={form.payment_date}
                onChange={handleChange}
                required
                className={INPUT}
              />
            </div>

            {/* Amount */}
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">
                Amount ($) *
              </label>
              <input
                name="amount"
                type="number"
                min={0}
                step={0.01}
                value={form.amount || ""}
                onChange={handleChange}
                required
                className={INPUT}
                placeholder="0.00"
              />
            </div>

            {/* Method */}
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">
                Method *
              </label>
              <select
                name="method"
                value={form.method}
                onChange={handleChange}
                className={INPUT + " cursor-pointer"}
              >
                {METHODS.map((m) => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">
                Status
              </label>
              <select
                name="status"
                value={form.status}
                onChange={handleChange}
                className={INPUT + " cursor-pointer"}
              >
                {STATUSES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
            </div>

            {/* Reference */}
            <div className="col-span-2">
              <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">
                Reference / Transaction ID
              </label>
              <input
                name="reference"
                value={form.reference}
                onChange={handleChange}
                className={INPUT}
                placeholder="Check #, Zelle confirmation, transaction ID…"
              />
            </div>

            {/* Notes */}
            <div className="col-span-2">
              <label className="text-slate-500 text-xs mb-1.5 block font-semibold uppercase tracking-wider">
                Internal Notes
              </label>
              <textarea
                name="notes"
                value={form.notes}
                onChange={handleChange}
                rows={2}
                maxLength={500}
                className={INPUT + " resize-none"}
                placeholder="Optional internal notes…"
              />
              <p className="text-slate-300 text-[10px] mt-1 text-right">{(form.notes || "").length}/500</p>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-5 py-2 bg-[#C8A96E] hover:bg-[#b8965a] text-white text-sm font-semibold rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              {isNew ? "Record Payment" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}